import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, RotateCcw, Save, ChevronLeft,
  Building2, Scale, Calculator, CheckCircle2, AlertTriangle, XCircle, Sparkles, BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackActivity } from '../lib/activity';
import { toast } from 'sonner';

// ---- Default inputs (Healthy mid-size company) ----
const ZERO_STATE = {
  revenue: 0, cogs: 0, opex: 0, interest: 0, tax: 0,
  currentAssets: 0, currentLiabilities: 0, inventory: 0,
  totalAssets: 0, totalEquity: 0, totalDebt: 0,
};

// 3 named presets the user can load
const PRESETS = {
  healthy_tech: {
    label: 'Healthy Tech Startup',
    description: 'High margins, strong cash, low debt — classic SaaS profile.',
    values: {
      revenue: 5000000, cogs: 1000000, opex: 1500000, interest: 50000, tax: 600000,
      currentAssets: 3500000, currentLiabilities: 800000, inventory: 100000,
      totalAssets: 6000000, totalEquity: 4500000, totalDebt: 500000,
    }
  },
  manufacturing: {
    label: 'Manufacturing Mid-Size',
    description: 'Inventory-heavy, moderate margins, balanced leverage.',
    values: {
      revenue: 20000000, cogs: 13000000, opex: 4000000, interest: 400000, tax: 700000,
      currentAssets: 7000000, currentLiabilities: 4000000, inventory: 3000000,
      totalAssets: 15000000, totalEquity: 7000000, totalDebt: 6000000,
    }
  },
  distressed_retail: {
    label: 'Distressed Retailer',
    description: 'Thin margins, weak liquidity, high leverage — turnaround case.',
    values: {
      revenue: 8000000, cogs: 6500000, opex: 1700000, interest: 350000, tax: 0,
      currentAssets: 1200000, currentLiabilities: 2500000, inventory: 900000,
      totalAssets: 4000000, totalEquity: 500000, totalDebt: 3000000,
    }
  },
};

// ---- Ratio health bands (Indian SME / general industry rules of thumb) ----
// Returns { tone, label, hint } based on the computed value
const evaluateRatio = (key, v) => {
  if (!isFinite(v)) return { tone: 'na', label: 'N/A', hint: 'Provide non-zero inputs to compute.' };
  switch (key) {
    case 'gross':       return v >= 0.40 ? { tone: 'good', label: 'Excellent' } : v >= 0.25 ? { tone: 'ok', label: 'Healthy' } : v >= 0.10 ? { tone: 'warn', label: 'Tight' } : { tone: 'bad', label: 'Concerning' };
    case 'operating':   return v >= 0.20 ? { tone: 'good', label: 'Excellent' } : v >= 0.10 ? { tone: 'ok', label: 'Healthy' } : v >= 0    ? { tone: 'warn', label: 'Thin' }  : { tone: 'bad', label: 'Operating loss' };
    case 'net':         return v >= 0.15 ? { tone: 'good', label: 'Excellent' } : v >= 0.05 ? { tone: 'ok', label: 'Healthy' } : v >= 0    ? { tone: 'warn', label: 'Marginal' } : { tone: 'bad', label: 'Net loss' };
    case 'current':     return v >= 2    ? { tone: 'good', label: 'Strong' }     : v >= 1.2  ? { tone: 'ok', label: 'Adequate' } : v >= 1 ? { tone: 'warn', label: 'Tight liquidity' } : { tone: 'bad', label: 'Liquidity risk' };
    case 'quick':       return v >= 1.5  ? { tone: 'good', label: 'Strong' }     : v >= 1    ? { tone: 'ok', label: 'Adequate' } : v >= 0.7 ? { tone: 'warn', label: 'Tight' } : { tone: 'bad', label: 'Cash crunch risk' };
    case 'roe':         return v >= 0.20 ? { tone: 'good', label: 'Excellent' } : v >= 0.10 ? { tone: 'ok', label: 'Healthy' } : v >= 0    ? { tone: 'warn', label: 'Below average' } : { tone: 'bad', label: 'Eroding equity' };
    case 'roa':         return v >= 0.10 ? { tone: 'good', label: 'Excellent' } : v >= 0.05 ? { tone: 'ok', label: 'Healthy' } : v >= 0    ? { tone: 'warn', label: 'Low' } : { tone: 'bad', label: 'Losses' };
    case 'de':          return v <= 0.5  ? { tone: 'good', label: 'Low leverage' } : v <= 1 ? { tone: 'ok', label: 'Moderate' } : v <= 2 ? { tone: 'warn', label: 'High leverage' } : { tone: 'bad', label: 'Over-leveraged' };
    case 'turnover':    return v >= 1.5  ? { tone: 'good', label: 'Efficient' }  : v >= 0.8  ? { tone: 'ok', label: 'Average' }    : v >= 0.4 ? { tone: 'warn', label: 'Slow' }      : { tone: 'bad', label: 'Asset-heavy' };
    default: return { tone: 'na', label: '—' };
  }
};

const fmtMoney = (n) => {
  if (!isFinite(n)) return '—';
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (Math.abs(n) >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
  if (Math.abs(n) >= 1000)     return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};
const fmtPct   = (v) => isFinite(v) ? `${(v * 100).toFixed(1)}%` : '—';
const fmtNum   = (v) => isFinite(v) ? v.toFixed(2) : '—';

const TONE_STYLES = {
  good: { ring: 'border-emerald-500/40', chip: 'bg-emerald-500/15 text-emerald-300', icon: CheckCircle2 },
  ok:   { ring: 'border-teal-500/30',    chip: 'bg-teal-500/15 text-teal-300',       icon: CheckCircle2 },
  warn: { ring: 'border-amber-500/40',   chip: 'bg-amber-500/15 text-amber-300',     icon: AlertTriangle },
  bad:  { ring: 'border-rose-500/40',    chip: 'bg-rose-500/15 text-rose-300',       icon: XCircle },
  na:   { ring: 'border-white/10',       chip: 'bg-white/10 text-white/50',          icon: Minus },
};

const InputField = ({ label, value, onChange, testId, hint }) => (
  <label className="block">
    <span className="text-xs text-white/55 uppercase tracking-wider">{label}</span>
    <div className="mt-1 flex items-center bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-teal-400/60">
      <span className="text-white/40 text-sm mr-2">₹</span>
      <input
        type="number"
        min={0}
        value={value === 0 ? '' : value}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        placeholder="0"
        data-testid={testId}
        className="bg-transparent text-white text-sm py-2 w-full outline-none"
      />
    </div>
    {hint && <span className="text-[10px] text-white/30 mt-1 block">{hint}</span>}
  </label>
);

const RatioCard = ({ title, value, formula, evalResult, testId }) => {
  const tone = TONE_STYLES[evalResult.tone];
  const Icon = tone.icon;
  return (
    <div className={`bg-[#11121f]/70 border ${tone.ring} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white/55 text-[11px] uppercase tracking-wider">{title}</p>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${tone.chip}`}>
          <Icon className="w-3 h-3" />
          {evalResult.label}
        </span>
      </div>
      <p className="text-2xl font-semibold text-white tabular-nums">{value}</p>
      <p className="text-[11px] text-white/40 mt-1.5">{formula}</p>
    </div>
  );
};

export default function FinanceSimulator() {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState(PRESETS.healthy_tech.values);
  const [touched, setTouched] = useState(false);

  // Fire-and-forget "started" event
  useEffect(() => {
    trackActivity({
      simulator_id: 'finance_ratios',
      simulator_name: 'Finance — Financial Ratios Simulator',
      action: 'started',
    });
  }, []);

  const set = (k) => (v) => { setTouched(true); setInputs((s) => ({ ...s, [k]: v })); };
  const loadPreset = (id) => {
    setInputs(PRESETS[id].values);
    setTouched(true);
    toast.success(`Loaded preset: ${PRESETS[id].label}`);
  };
  const reset = () => { setInputs(ZERO_STATE); setTouched(false); };

  // ---- Computed ratios ----
  const r = useMemo(() => {
    const rev = inputs.revenue || 0;
    const grossProfit = rev - (inputs.cogs || 0);
    const operatingProfit = grossProfit - (inputs.opex || 0);
    const netIncome = operatingProfit - (inputs.interest || 0) - (inputs.tax || 0);

    const ratios = {
      gross:       rev ? grossProfit / rev : NaN,
      operating:   rev ? operatingProfit / rev : NaN,
      net:         rev ? netIncome / rev : NaN,
      current:     inputs.currentLiabilities ? (inputs.currentAssets || 0) / inputs.currentLiabilities : NaN,
      quick:       inputs.currentLiabilities ? ((inputs.currentAssets || 0) - (inputs.inventory || 0)) / inputs.currentLiabilities : NaN,
      roe:         inputs.totalEquity ? netIncome / inputs.totalEquity : NaN,
      roa:         inputs.totalAssets ? netIncome / inputs.totalAssets : NaN,
      de:          inputs.totalEquity ? (inputs.totalDebt || 0) / inputs.totalEquity : NaN,
      turnover:    inputs.totalAssets ? rev / inputs.totalAssets : NaN,
    };
    return { ratios, grossProfit, operatingProfit, netIncome };
  }, [inputs]);

  const healthScore = useMemo(() => {
    const keys = Object.keys(r.ratios);
    let good = 0, total = 0;
    keys.forEach((k) => {
      const ev = evaluateRatio(k, r.ratios[k]);
      if (ev.tone === 'na') return;
      total += 1;
      if (ev.tone === 'good' || ev.tone === 'ok') good += 1;
    });
    return total ? Math.round((good / total) * 100) : 0;
  }, [r.ratios]);

  const balanceSheetCheck = useMemo(() => {
    const lhs = inputs.totalAssets || 0;
    const rhs = (inputs.totalEquity || 0) + (inputs.totalDebt || 0);
    if (lhs === 0 && rhs === 0) return null;
    const diff = Math.abs(lhs - rhs);
    const pct = lhs ? (diff / lhs) * 100 : 100;
    return { ok: pct <= 2, diff, pct };
  }, [inputs]);

  const saveSession = async () => {
    await trackActivity({
      simulator_id: 'finance_ratios',
      simulator_name: 'Finance — Financial Ratios Simulator',
      action: 'completed',
      score: healthScore,
      metadata: { revenue: inputs.revenue, net_income: r.netIncome, healthScore },
    });
    toast.success(`Saved — Financial Health Score: ${healthScore}/100`);
  };

  // ---- Render ----
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#0d1025] to-[#0a0a1a] text-white">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/10 blur-3xl pointer-events-none" />

      {/* Top bar */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-6 pb-3 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/60 hover:text-white text-sm" data-testid="fin-back-btn">
          <ChevronLeft className="w-4 h-4" /> Back to home
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reset} className="border-white/10 text-white/70 hover:bg-white/[0.04]" data-testid="fin-reset-btn">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <Button onClick={saveSession} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400" data-testid="fin-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> Save & Track
          </Button>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-12">
        {/* Title row */}
        <div className="mb-6">
          <p className="text-xs text-emerald-400 tracking-widest uppercase mb-1">Finance Simulator</p>
          <h1 className="text-3xl md:text-4xl font-bold">Financial Ratios &amp; Health Check</h1>
          <p className="text-white/55 text-sm mt-1.5 max-w-3xl">
            Enter a company&apos;s P&amp;L + Balance Sheet figures (in ₹). The simulator computes 9 key ratios live and grades the overall health.
            Switch between 3 ready presets to see how a tech startup vs a manufacturer vs a distressed retailer looks.
          </p>
        </div>

        {/* Preset row */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="fin-presets">
          <span className="text-xs text-white/50 uppercase tracking-wider mr-1">Load preset:</span>
          {Object.entries(PRESETS).map(([id, p]) => (
            <button
              key={id}
              onClick={() => loadPreset(id)}
              data-testid={`fin-preset-${id}`}
              className="px-3 py-1.5 rounded-full text-xs bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-emerald-400/40 transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT — Inputs */}
          <div className="lg:col-span-5 space-y-5">
            {/* Income Statement */}
            <div className="bg-[#11121f]/70 border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-4 h-4 text-emerald-400" />
                <h2 className="text-base font-semibold">Income Statement</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Revenue"          value={inputs.revenue}  onChange={set('revenue')}  testId="fin-input-revenue" />
                <InputField label="COGS"             value={inputs.cogs}     onChange={set('cogs')}     testId="fin-input-cogs" />
                <InputField label="Operating Exp."   value={inputs.opex}     onChange={set('opex')}     testId="fin-input-opex" />
                <InputField label="Interest"         value={inputs.interest} onChange={set('interest')} testId="fin-input-interest" />
                <InputField label="Tax"              value={inputs.tax}      onChange={set('tax')}      testId="fin-input-tax" />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5 text-center">
                <div><p className="text-[10px] text-white/40 uppercase">Gross Profit</p><p className="text-sm font-semibold text-white mt-0.5">{fmtMoney(r.grossProfit)}</p></div>
                <div><p className="text-[10px] text-white/40 uppercase">Operating</p><p className="text-sm font-semibold text-white mt-0.5">{fmtMoney(r.operatingProfit)}</p></div>
                <div><p className="text-[10px] text-white/40 uppercase">Net Income</p>
                  <p className={`text-sm font-semibold mt-0.5 inline-flex items-center gap-1 ${r.netIncome >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {r.netIncome >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {fmtMoney(r.netIncome)}
                  </p>
                </div>
              </div>
            </div>

            {/* Balance Sheet */}
            <div className="bg-[#11121f]/70 border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Scale className="w-4 h-4 text-cyan-400" />
                <h2 className="text-base font-semibold">Balance Sheet</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Current Assets"      value={inputs.currentAssets}      onChange={set('currentAssets')}      testId="fin-input-ca" />
                <InputField label="Current Liabilities" value={inputs.currentLiabilities} onChange={set('currentLiabilities')} testId="fin-input-cl" />
                <InputField label="Inventory"           value={inputs.inventory}          onChange={set('inventory')}          testId="fin-input-inv" hint="Part of Current Assets" />
                <InputField label="Total Assets"        value={inputs.totalAssets}        onChange={set('totalAssets')}        testId="fin-input-ta" />
                <InputField label="Total Equity"        value={inputs.totalEquity}        onChange={set('totalEquity')}        testId="fin-input-te" />
                <InputField label="Total Debt"          value={inputs.totalDebt}          onChange={set('totalDebt')}          testId="fin-input-td" />
              </div>
              {balanceSheetCheck && (
                <div
                  className={`mt-4 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${
                    balanceSheetCheck.ok
                      ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                      : 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                  }`}
                  data-testid="fin-bs-check"
                >
                  {balanceSheetCheck.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  Balance Sheet check:&nbsp;
                  <span className="font-medium">{balanceSheetCheck.ok ? 'Balanced (Assets = Equity + Debt)' : `Off by ${fmtMoney(balanceSheetCheck.diff)} (${balanceSheetCheck.pct.toFixed(1)}%)`}</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Ratios */}
          <div className="lg:col-span-7 space-y-5">
            {/* Health score header */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-400/20 rounded-2xl p-5 flex items-center justify-between" data-testid="fin-health-score">
              <div>
                <p className="text-[11px] text-emerald-300 uppercase tracking-wider mb-1">Financial Health Score</p>
                <p className="text-4xl font-bold text-white">{healthScore}<span className="text-base text-white/40">/100</span></p>
                <p className="text-xs text-white/55 mt-1">{touched ? 'Live-computed from your inputs across 9 ratios.' : 'Showing a healthy tech startup by default.'}</p>
              </div>
              <Sparkles className="w-12 h-12 text-emerald-400/40" />
            </div>

            {/* Ratios grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="fin-ratios-grid">
              <RatioCard testId="ratio-gross"     title="Gross Margin"        value={fmtPct(r.ratios.gross)}     formula="(Rev − COGS) / Rev"            evalResult={evaluateRatio('gross', r.ratios.gross)} />
              <RatioCard testId="ratio-operating" title="Operating Margin"    value={fmtPct(r.ratios.operating)} formula="(Rev − COGS − Opex) / Rev"     evalResult={evaluateRatio('operating', r.ratios.operating)} />
              <RatioCard testId="ratio-net"       title="Net Profit Margin"   value={fmtPct(r.ratios.net)}       formula="Net Income / Rev"              evalResult={evaluateRatio('net', r.ratios.net)} />
              <RatioCard testId="ratio-current"   title="Current Ratio"       value={fmtNum(r.ratios.current)}   formula="Current Assets / Current Liab" evalResult={evaluateRatio('current', r.ratios.current)} />
              <RatioCard testId="ratio-quick"     title="Quick Ratio"         value={fmtNum(r.ratios.quick)}     formula="(CA − Inv) / CL"               evalResult={evaluateRatio('quick', r.ratios.quick)} />
              <RatioCard testId="ratio-roe"       title="Return on Equity"    value={fmtPct(r.ratios.roe)}       formula="Net Income / Equity"           evalResult={evaluateRatio('roe', r.ratios.roe)} />
              <RatioCard testId="ratio-roa"       title="Return on Assets"    value={fmtPct(r.ratios.roa)}       formula="Net Income / Total Assets"     evalResult={evaluateRatio('roa', r.ratios.roa)} />
              <RatioCard testId="ratio-de"        title="Debt-to-Equity"      value={fmtNum(r.ratios.de)}        formula="Total Debt / Equity"           evalResult={evaluateRatio('de', r.ratios.de)} />
              <RatioCard testId="ratio-turnover"  title="Asset Turnover"      value={fmtNum(r.ratios.turnover)}  formula="Revenue / Total Assets"        evalResult={evaluateRatio('turnover', r.ratios.turnover)} />
            </div>

            {/* Footer explainer */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-white/60 leading-relaxed">
                <p className="text-white/80 font-medium mb-1">Quick legend</p>
                Green = healthy / excellent · Teal = adequate · Amber = watchlist · Red = concerning. Bands follow general Indian SME &amp; CFA Level-1 rules of thumb. Industry-specific benchmarks may differ — use as a learning aid.
              </div>
            </div>
          </div>
        </div>

        {/* Sticky CTA bar on small screens */}
        <div className="mt-8 flex items-center justify-between bg-[#11121f]/70 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Building2 className="w-4 h-4 text-emerald-400" />
            Try modifying any input — ratios update live.
          </div>
          <Button onClick={saveSession} className="bg-gradient-to-r from-emerald-500 to-teal-500" data-testid="fin-save-btn-2">
            Save Session
          </Button>
        </div>
      </div>
    </div>
  );
}
